import React from "react";
import styled from "styled-components";
import { colors2 } from "oolib";
import { UI_CAPTION_SEMIBOLD_DF } from "oolib/dist/v2/components/Typo2";
import { Info } from "@phosphor-icons/react";

/**
 * Content shown blurred and inert behind a note — for a widget whose data
 * cannot say anything yet (a trend over a single month), so the reader sees
 * the shape of what it would show and why it doesn't. Wraps whatever it is
 * given; the DashboardWidgetShell uses it for its body, the Priorities zone
 * strip for its row of cards.
 *
 *   note   the text on the pill; nothing is blurred when it is empty
 *   icon   the phosphor icon on the pill (Info by default)
 *
 * BlurNotePill is the note on its own, for a surface that blurs only part
 * of itself (the triage field keeps its bubbles and severity axis).
 */
const Wrap = styled.div`
  position: relative;
  overflow: hidden;
`;

/* Blurred enough that the numbers can't be read, clear enough that the
   shape comes through. Inert, so nothing under it can be hovered or clicked. */
const Blurred = styled.div`
  height: 100%;
  filter: blur(4px);
  opacity: 0.7;
  pointer-events: none;
  user-select: none;
`;

const NoteLayer = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1.6rem;
`;

/* The note itself: the report widgets' error pill (icon + semibold caption
   on a tinted, glowing chip), in a neutral tone — the glow lifts it off the
   blurred content. */
const Pill = styled.div`
  display: flex;
  align-items: center;
  gap: 0.8rem;
  max-width: 34rem;
  padding: 1rem 1.2rem;
  border-radius: 0.6rem;
  background: ${colors2.white};
  border: 1px solid ${colors2.grey10};
  box-shadow: 0 0 120px 30px ${colors2.white};
  color: ${colors2.grey80};
`;

export const BlurNotePill = ({ note, icon: NoteIcon = Info, style }) => (
  <Pill style={style}>
    <NoteIcon size={18} weight="bold" color={colors2.grey60} style={{ flexShrink: 0 }} />
    <UI_CAPTION_SEMIBOLD_DF>{note}</UI_CAPTION_SEMIBOLD_DF>
  </Pill>
);

export const BlurNoteOverlay = ({ note, icon, children, style }) => {
  if (!note) return children;
  return (
    <Wrap style={style}>
      <Blurred aria-hidden="true">{children}</Blurred>
      <NoteLayer>
        <BlurNotePill note={note} icon={icon} />
      </NoteLayer>
    </Wrap>
  );
};
